/**
 * BoardMembersPanel — the members a board allows: its board members, the
 * pending board invites and the actions to change them.
 *
 * The body lives here so the board settings "Members" tab and the
 * `/w/:wsId/p/:pKey/members` screen share one implementation.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import { toast } from '@/features/feedback/toast'
import { useProfiles, pickDisplay } from '@/features/profiles/profiles'
import { ConfirmDialog } from '@/components/feedback'
import { useWorkspaces } from '@/features/workspaces/workspaces'
import type { ProjectMember, Project, WorkspaceMember } from '@friday/shared/realtime'
import { cn } from '@/lib/utils'
import { UserAvatar } from '@/components/user/UserAvatar'

interface ProjectInvite {
  id: string
  projectId: string
  email: string
  role: 'lead' | 'contributor' | 'viewer'
  invitedBy: string
  expiresAt: string
  acceptedAt: string | null
  createdAt: string
}

interface InviteCreateResponse {
  id: string
  inviteUrl: string
  email: string
  role: string
}

const ROLES = [
  { value: 'lead', label: 'Lead', desc: 'Full control. Can rename project, manage members.' },
  { value: 'contributor', label: 'Contributor', desc: 'Can edit tasks, sprints, milestones.' },
  { value: 'viewer', label: 'Viewer', desc: 'Read-only.' },
] as const

type ProjectRole = (typeof ROLES)[number]['value']

export interface BoardMembersPanelProps {
  wsId: string
  /** The board whose members are managed here. */
  project: Project
}

export function BoardMembersPanel({ wsId, project }: BoardMembersPanelProps) {
  const qc = useQueryClient()

  const membersQ = useQuery({
    queryKey: ['project-members', project?.id],
    queryFn: () => api<ProjectMember[]>(`/api/v1/projects/${project!.id}/members`),
    enabled: !!project,
  })

  const invitesQ = useQuery({
    queryKey: ['project-invites', project?.id],
    queryFn: () => api<ProjectInvite[]>(`/api/v1/projects/${project!.id}/invites`),
    enabled: !!project,
  })

  // Workspace members — used as the source for the "Add existing member" picker.
  const wsMembersQ = useQuery({
    queryKey: ['workspace-members', wsId],
    queryFn: () => api<WorkspaceMember[]>(`/api/v1/workspaces/${wsId}/members`),
    enabled: !!wsId,
  })
  const workspacesQ = useWorkspaces()
  const workspaceName = workspacesQ.data?.find((w) => w.id === wsId)?.name ?? 'this workspace'

  const updateRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ProjectRole }) =>
      api(`/api/v1/projects/${project!.id}/members/${userId}`, { method: 'PATCH', body: { role } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-members', project?.id] })
      toast('Role updated', { kind: 'success' })
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message
      toast(msg, { kind: 'error' })
    },
  })
  const removeMember = useMutation({
    mutationFn: (userId: string) => api(`/api/v1/projects/${project!.id}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-members', project?.id] })
      toast('Member removed', { kind: 'success' })
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message
      toast(msg, { kind: 'error' })
    },
  })
  const revokeInvite = useMutation({
    mutationFn: (id: string) => api(`/api/v1/projects/${project!.id}/invites/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-invites', project?.id] })
      toast('Invite revoked', { kind: 'success' })
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message
      toast(msg, { kind: 'error' })
    },
  })
  const addMember = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ProjectRole }) =>
      api(`/api/v1/projects/${project!.id}/members`, { method: 'POST', body: { userId, role } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-members', project?.id] })
      setAddingMember(false)
      setChosenUserId('')
      toast('Member added', { kind: 'success' })
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message
      toast(msg, { kind: 'error' })
    },
  })

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<ProjectRole>('contributor')
  const [justInvited, setJustInvited] = useState<InviteCreateResponse | null>(null)
  const [pendingRemove, setPendingRemove] = useState<string | null>(null)
  const [addingMember, setAddingMember] = useState(false)
  const [chosenUserId, setChosenUserId] = useState('')
  const [addRole, setAddRole] = useState<ProjectRole>('contributor')

  const invite = useMutation({
    mutationFn: () =>
      api<InviteCreateResponse>(`/api/v1/projects/${project!.id}/invites`, {
        method: 'POST',
        body: { email: email.trim(), role },
      }),
    onSuccess: (data) => {
      setJustInvited(data)
      setEmail('')
      void qc.invalidateQueries({ queryKey: ['project-invites', project?.id] })
      toast('Invite created', { kind: 'success' })
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message
      toast(msg, { kind: 'error' })
    },
  })

  if (!project) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-surface-500">
        Loading project…
      </div>
    )
  }

  const members = membersQ.data ?? []
  const memberIds = members.map((m) => m.userId)
  const { profiles } = useProfiles(memberIds)

  // Workspace members who are not yet in this project.
  const projectMemberIds = new Set(memberIds)
  const availableWsMembers = (wsMembersQ.data ?? []).filter((w) => !projectMemberIds.has(w.userId))

  return (
    <>
      <div>
        {/* Invite by email */}
        <section className="card mb-4 p-4">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-surface-500">
            Invite by email
          </h2>
          <p className="mb-3 text-xs text-surface-500">
            Send an invite link. They must already have a Friday account (or sign up). Invites expire in 14 days.
          </p>
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={async (e) => {
              e.preventDefault()
              if (!email.trim()) return
              try {
                await invite.mutateAsync()
              } catch {
                /* error already toasted */
              }
            }}
          >
            <label className="flex-1 min-w-[16rem]">
              <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Email</span>
              <input
                type="email"
                required
                className="input mt-1"
                placeholder="teammate@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label>
              <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Role</span>
              <select
                className="input mt-1"
                value={role}
                onChange={(e) => setRole(e.target.value as ProjectRole)}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit" className="btn-primary" disabled={invite.isPending || !email.trim()}>
              {invite.isPending ? 'Sending…' : 'Send invite'}
            </button>
          </form>

          {justInvited && (
            <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
              <div className="mb-1 font-medium text-amber-700 dark:text-amber-300">
                Invite link (copy &amp; send)
              </div>
              <code className="block break-all rounded bg-amber-500/10 p-2 font-mono">
                {window.location.origin}
                {justInvited.inviteUrl}
              </code>
              <button
                className="mt-2 text-amber-700 dark:text-amber-300 hover:underline"
                onClick={() => {
                  navigator.clipboard?.writeText(window.location.origin + justInvited.inviteUrl)
                  toast('Copied', { kind: 'success' })
                }}
              >
                Copy link
              </button>
            </div>
          )}
        </section>

        {/* Add existing workspace member */}
        <section className="card mb-4 p-4">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-surface-500">
            Add existing workspace member
          </h2>
          <p className="mb-3 text-xs text-surface-500">
            People who already belong to <strong>{workspaceName}</strong> can be added directly without an invite.
          </p>
          {!addingMember ? (
            <button onClick={() => setAddingMember(true)} className="btn-ghost text-xs">
              + Add member
            </button>
          ) : (
            <div className="space-y-2">
              {availableWsMembers.length === 0 ? (
                <p className="text-xs text-surface-500">
                  All workspace members are already in this project.
                </p>
              ) : (
                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex-1 min-w-[14rem]">
                    <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">
                      Workspace member
                    </span>
                    <select
                      className="input mt-1"
                      value={chosenUserId}
                      onChange={(e) => setChosenUserId(e.target.value)}
                    >
                      <option value="">Select someone…</option>
                      {availableWsMembers.map((wm) => (
                        <option key={wm.userId} value={wm.userId}>
                          {wm.userId}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Role</span>
                    <select
                      className="input mt-1"
                      value={addRole}
                      onChange={(e) => setAddRole(e.target.value as ProjectRole)}
                    >
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setAddingMember(false)
                        setChosenUserId('')
                      }}
                      className="btn-ghost text-xs"
                      disabled={addMember.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => chosenUserId && addMember.mutate({ userId: chosenUserId, role: addRole })}
                      className="btn-primary text-xs"
                      disabled={!chosenUserId || addMember.isPending}
                    >
                      {addMember.isPending ? 'Adding…' : 'Add to project'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Pending invites */}
        <section className="card mb-4 divide-y divide-surface-200 dark:divide-surface-700">
          <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-surface-500">
            Pending invites
          </div>
          {(invitesQ.data ?? []).length === 0 && (
            <div className="px-4 py-3 text-sm text-surface-500">No pending invites.</div>
          )}
          {(invitesQ.data ?? []).map((inv) => (
            <div key={inv.id} className="flex items-center justify-between px-4 py-3 text-sm">
              <div>
                <div className="font-medium">{inv.email}</div>
                <div className="text-xs text-surface-500">
                  {inv.role} · expires {new Date(inv.expiresAt).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => revokeInvite.mutate(inv.id)}
                className="btn-ghost text-xs text-red-600 dark:text-red-300"
                disabled={revokeInvite.isPending}
              >
                Revoke
              </button>
            </div>
          ))}
        </section>

        {/* Members */}
        <section className="card divide-y divide-surface-200 dark:divide-surface-700">
          <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-surface-500">
            Members ({members.length})
          </div>
          {members.map((m) => {
            const profile = profiles[m.userId]
            const display = pickDisplay(m.userId, profile)
            return (
              <div key={m.userId} className="flex items-center justify-between px-4 py-3 text-sm">
                <div className="flex items-center gap-3">
                  <UserAvatar name={display.displayName} email={display.email} sources={display.avatarSources} className="h-7 w-7 text-[10px]" pixelSize={56} decorative />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{display.displayName}</div>
                    <div className="text-[10px] text-surface-500">{m.userId}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    className={cn('input py-0.5 text-xs')}
                    value={m.role}
                    onChange={(e) =>
                      updateRole.mutate({ userId: m.userId, role: e.target.value as ProjectRole })
                    }
                  >
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setPendingRemove(m.userId)}
                    className="btn-ghost text-xs text-red-600 dark:text-red-300"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )
          })}
          {(membersQ.isLoading || invitesQ.isLoading) && (
            <div className="px-4 py-3 text-sm text-surface-500">Loading…</div>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={!!pendingRemove}
        title="Remove this member?"
        body={
          <p className="text-sm text-surface-600 dark:text-surface-300">
            They will lose access to {project.key}. Their tasks stay intact and
            they remain a workspace member.
          </p>
        }
        confirmLabel="Remove member"
        destructive
        busy={removeMember.isPending}
        onClose={() => !removeMember.isPending && setPendingRemove(null)}
        onConfirm={async () => {
          if (!pendingRemove) return
          try {
            await removeMember.mutateAsync(pendingRemove)
            setPendingRemove(null)
          } catch {
            /* error already toasted */
          }
        }}
      />
    </>
  )
}
